import { useState } from "react";
import { Modal } from "@/components/Modal";
import {
  type MoveTarget,
  type MoveTargetGroup,
  moveTargetLabel,
} from "@/lib/days";
import type { Todo } from "@/lib/schema";

/**
 * What the move flow knows about a target week. A `missing` week is created by
 * the confirm itself (docs/adr/0023), so its days stay pickable and the group
 * header says so; a `no-widget` week exists but dropped this widget instance
 * from its snapshot, so nothing can be moved into it at all.
 */
export type WeekStatus = "exists" | "missing" | "no-widget";

interface Props {
  open: boolean;
  widgetId: string;
  /** Read-only: this is the open group, a view over the stored array. */
  todos: readonly Todo[];
  groups: MoveTargetGroup[];
  weekStatus: (year: number, weekNumber: number) => WeekStatus;
  onCancel: () => void;
  onConfirm: (ids: string[], target: MoveTarget) => Promise<void>;
}

export function MoveTodosModal(props: Props) {
  // Remounting on open is what resets the selection — the body initialises its
  // state from props, so there is no effect to keep in step with them.
  return (
    <Modal
      open={props.open}
      onClose={props.onCancel}
      data-testid={`move-todos-modal-${props.widgetId}`}
      size="lg"
    >
      {props.open ? <MoveTodosBody {...props} /> : null}
    </Modal>
  );
}

function MoveTodosBody({
  widgetId,
  todos,
  groups,
  weekStatus,
  onCancel,
  onConfirm,
}: Props) {
  const [selected, setSelected] = useState<string[]>(() =>
    todos.map((t) => t.id),
  );
  const [targetDate, setTargetDate] = useState<string>(() => {
    for (const group of groups) {
      if (weekStatus(group.year, group.weekNumber) === "no-widget") continue;
      // `days` opens with the day the card belongs to whenever that day falls
      // first in the window — it is drawn, never selected, so skip past it.
      const first = group.days.find((d) => d.target);
      if (first) return first.date;
    }
    return "";
  });
  const [moving, setMoving] = useState(false);

  const allSelected = selected.length === todos.length && todos.length > 0;

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setSelected(allSelected ? [] : todos.map((t) => t.id));

  const target =
    groups.flatMap((g) => g.days).find((d) => d.date === targetDate)?.target ??
    null;

  const confirm = async () => {
    if (moving || !target || selected.length === 0) return;
    setMoving(true);
    try {
      await onConfirm(selected, target);
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <h2>Flytt oppgaver</h2>
      <p className="modal-body">
        Velg oppgavene du vil flytte, og hvilken dag de skal til.
      </p>

      <section className="move-todos-step">
        <h3 className="move-todos-step-head">1 · Hvilke oppgaver?</h3>
        <button
          type="button"
          className="move-todos-toggle"
          onClick={toggleAll}
          data-testid={`move-todos-toggle-all-${widgetId}`}
        >
          {allSelected ? "Fjern alle" : "Velg alle"}
        </button>
        <ul className="move-todos-list">
          {todos.map((todo) => (
            <li key={todo.id}>
              <label className="move-todos-row">
                <input
                  type="checkbox"
                  checked={selected.includes(todo.id)}
                  onChange={() => toggle(todo.id)}
                  data-testid={`move-todos-item-${todo.id}`}
                />
                <span>{todo.label}</span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="move-todos-step">
        <h3 className="move-todos-step-head">2 · Hvilken dag?</h3>
        {groups.map((group) => {
          const status = weekStatus(group.year, group.weekNumber);
          return (
            <div
              key={`${group.year}-${group.weekNumber}`}
              className="move-todos-group"
              data-testid={`move-todos-group-${group.year}-${group.weekNumber}`}
            >
              <p className="move-todos-group-head">
                {group.label}
                {status === "missing" ? (
                  <span className="move-todos-note"> (opprettes)</span>
                ) : null}
                {status === "no-widget" ? (
                  <span className="move-todos-note">
                    {" "}
                    (widgeten finnes ikke i denne uken)
                  </span>
                ) : null}
              </p>
              {/* Real radios inside pill-shaped labels: the group keeps
                  native arrow-key navigation, the pill keeps the look. */}
              <div className="move-todos-days">
                {group.days.map((day) => (
                  <label
                    key={day.date}
                    className="day-pill"
                    data-selected={targetDate === day.date ? "true" : "false"}
                    data-current={day.target ? undefined : "true"}
                    data-testid={`move-todos-day-${day.date}`}
                  >
                    <input
                      type="radio"
                      name={`move-target-${widgetId}`}
                      className="day-pill-input"
                      checked={targetDate === day.date}
                      disabled={!day.target || status === "no-widget"}
                      onChange={() => setTargetDate(day.date)}
                    />
                    {moveTargetLabel(day)}
                    {day.target ? null : (
                      <span className="move-todos-note"> (denne dagen)</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onCancel}
          data-testid={`move-todos-cancel-${widgetId}`}
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void confirm()}
          disabled={selected.length === 0 || !target || moving}
          data-testid={`move-todos-confirm-${widgetId}`}
        >
          Flytt
        </button>
      </div>
    </>
  );
}
